import { useState } from "react";
import { toast } from "sonner";
import { MailCheck, LogOut } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const GENERI = [["donna", "Donna"], ["uomo", "Uomo"], ["non_specificato", "Preferisco non specificarlo"]];

// Completamento obbligatorio del profilo per gli account esistenti: chiede SOLO i dati
// mancanti (verifica email, data di nascita 18+, genere) senza sovrascrivere nulla.
// Non saltabile: le funzioni di Arcana richiedono questi dati.
export default function CompletaProfilo() {
  const { user, refresh, logout } = useAuth();
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [gender, setGender] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resent, setResent] = useState(false);

  if (!user || user.role === "admin") return null;
  const needEmail = !user.email_verified;
  const needBirth = !user.birth_date;
  const needGender = !user.gender;
  if (!needEmail && !needBirth && !needGender) return null;

  const iso = day && month && year ? `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}` : "";

  const resend = async () => {
    try {
      await api.post("/auth/verify-email/resend");
      setResent(true);
      toast.success("Email di verifica inviata di nuovo.");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (needBirth) await api.put("/profile/birthdate", { birth_date: iso });
      if (needGender) await api.put("/profile/gender", { gender });
      await refresh();
      toast.success("Grazie! Il tuo profilo è aggiornato.");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const canSave = (!needBirth || (iso && confirmed)) && (!needGender || !!gender);
  const hasForm = needBirth || needGender;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-[#0f0617]/85 backdrop-blur-sm overflow-y-auto" data-testid="completa-profilo">
      <div className="mystic-card p-6 sm:p-8 w-full max-w-md my-auto">
        <div className="text-center mb-5">
          <MailCheck className="w-7 h-7 text-amber-400 mx-auto mb-2" />
          <h2 className="font-display text-xl text-amber-100">Ancora un piccolo passo</h2>
          <p className="text-purple-200/70 text-sm mt-2 leading-relaxed">
            Per usare Arcana ci servono questi dati: li chiediamo una sola volta e soltanto se mancano.
          </p>
        </div>

        {needEmail && (
          <div className="mb-5" data-testid="completa-email-block">
            <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">Verifica la tua email</p>
            <p className="text-xs text-purple-200/70 leading-relaxed mb-3">
              Ti abbiamo inviato un link di conferma a <span className="text-amber-200">{user.email}</span>.
              Aprilo per attivare l'account. Controlla anche la cartella spam.
            </p>
            <div className="flex flex-wrap gap-2">
              <button onClick={resend} disabled={resent} data-testid="completa-resend-btn"
                className="text-xs text-purple-300/60 hover:text-amber-300 border border-[#4a2175] rounded-full px-4 py-2 transition-colors disabled:opacity-50">
                {resent ? "Email inviata di nuovo" : "Invia di nuovo l'email"}
              </button>
              <button onClick={refresh} data-testid="completa-verified-btn"
                className="text-xs text-purple-300/60 hover:text-amber-300 border border-[#4a2175] rounded-full px-4 py-2 transition-colors">
                Ho già verificato
              </button>
            </div>
          </div>
        )}

        {needBirth && (
          <div className="mb-5" data-testid="completa-birth-block">
            <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">La tua data di nascita</p>
            <div className="grid grid-cols-3 gap-2">
              <select value={day} onChange={(e) => setDay(e.target.value)} data-testid="completa-birth-day" className="input-dark">
                <option value="">Giorno</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="completa-birth-month" className="input-dark">
                <option value="">Mese</option>
                {MESI.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="completa-birth-year" className="input-dark">
                <option value="">Anno</option>
                {Array.from({ length: 108 }, (_, i) => new Date().getFullYear() - 18 - i).map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid="completa-age-confirm-label">
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="completa-age-confirm" className="mt-0.5 accent-amber-500" />
              <span className="text-[11px] text-purple-200/70 leading-relaxed">
                Confermo che la data di nascita è corretta e dichiaro di avere almeno 18 anni.
              </span>
            </label>
          </div>
        )}

        {needGender && (
          <div className="mb-5" data-testid="completa-gender-block">
            <p className="font-cinzel text-[11px] tracking-[0.25em] uppercase text-amber-400/80 mb-2">Come ti identifichi?</p>
            <div className="grid grid-cols-1 gap-1.5">
              {GENERI.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setGender(v)} data-testid={`completa-gender-${v}`}
                  className={`px-4 py-2.5 rounded-md text-sm text-left border transition-colors ${gender === v ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-[#4a2175] bg-[#180a26] text-purple-200/80 hover:border-amber-500/30"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 mt-5">
          {hasForm && (
            <button onClick={save} disabled={!canSave || busy} data-testid="completa-save-btn" className="gold-btn w-full py-2.5 text-sm disabled:opacity-50">
              {busy ? "Attendi…" : "Salva e continua"}
            </button>
          )}
          <button onClick={logout} data-testid="completa-logout-btn"
            className="text-xs text-purple-300/60 hover:text-amber-300 transition-colors py-1 inline-flex items-center justify-center gap-1.5">
            <LogOut className="w-3.5 h-3.5" /> Esci
          </button>
        </div>
      </div>
    </div>
  );
}
